import React from "react";
import {
  IGroupValidationResult,
  IInputConfig,
  Input,
  InputGroup,
  maxLength,
  pattern,
  Patterns,
  requiredNonEmptyString,
  TextArea,
  WatchedFunction,
} from "component-library";

import {
  validateAmountOfSplitValues,
  validateSplitStringItemsLength,
  validateSplitStringOfEmails,
} from "shared/utils/form/validators/string-split-validatior";

import "./EmailForm.scss";

export interface IEmailFormContent {
  emailFormSenderNameLabel: string;
  emailFormSenderNameRequired: string;
  emailFormSenderNameMaxLength: string;
  emailFormSenderEmailLabel: string;
  emailFormSenderEmailRequired: string;
  emailFormSenderEmailValidEmail: string;
  emailFormSenderEmailMaxLength: string;
  emailFormRecipientsLabel: string;
  emailFormRecipientsRequired: string;
  emailFormRecipientsSingleMaxLength: string;
  emailFormRecipientsSingleValid: string;
  emailFormRecipientsMaxCount: string;
  emailFormSubjectLabel: string;
  emailFormSubjectRequired: string;
  emailFormSubjectMaxLength: string;
  emailFormMessageLabel: string;
  emailFormAdditionalText: string;
}

export interface IEmailFormProps {
  updateForm: (form: IEmailForm) => void;
  updateFormValidity: (isFormValid: boolean) => void;
  initialForm: IEmailForm;
  content: IEmailFormContent;
  isFormSubmitted: boolean;
}

interface IEmailFormState {
  form: IEmailForm;
  validationData: IEmailFormValidation;
}

export interface IEmailForm {
  message: string;
  subject: string;
  recipients: string;
  senderName: string;
  senderEmail: string;
}

declare type IEmailFormValidation = {
  [T in keyof IEmailForm]: {
    invalid: boolean;
    validationMessage: string;
  };
};

const GET_VALIDATION_DEFAULT = () => ({
  invalid: false,
  validationMessage: "",
});

const VALIDATE = new WatchedFunction(() => undefined);

const MAX_MESSAGE_LENGTH = 10000;
const MAX_EMAIL_LENGTH = 254;
const MAX_NAME_LENGTH = 250;
const MAX_SUBJECT_LENGTH = 500;
const MAX_RECIPIENTS_COUNT = 500;

export default class EmailForm extends React.Component<
  IEmailFormProps,
  IEmailFormState
> {
  constructor(props: IEmailFormProps) {
    super(props);

    this.state = {
      form: { ...props.initialForm },
      validationData: {
        message: GET_VALIDATION_DEFAULT(),
        subject: GET_VALIDATION_DEFAULT(),
        recipients: GET_VALIDATION_DEFAULT(),
        senderName: GET_VALIDATION_DEFAULT(),
        senderEmail: GET_VALIDATION_DEFAULT(),
      },
    };
  }

  public render() {
    const { content } = this.props;
    const { form } = this.state;
    const formConfig = this.getFormConfiguration(content, form);

    return (
      <InputGroup
        className="c-email-form"
        inputConfigs={formConfig}
        validationDone={this.validationPerformed}
        triggerValidation={VALIDATE}
      >
        {this.renderInput("senderName", content.emailFormSenderNameLabel)}
        {this.renderInput("senderEmail", content.emailFormSenderEmailLabel)}
        {this.renderInput("recipients", content.emailFormRecipientsLabel)}
        {this.renderInput("subject", content.emailFormSubjectLabel)}
        <TextArea
          id="emailFormTextArea"
          label={content.emailFormMessageLabel}
          value={form.message}
          valueChanged={this.inputChanged("message")}
          autoResize
          showsCharacterCount
          maxLength={MAX_MESSAGE_LENGTH}
        />
        <div className="c-email-form__additional-text">
          {content.emailFormAdditionalText}
        </div>
      </InputGroup>
    );
  }

  public validationPerformed = (result: IGroupValidationResult) => {
    const validationData = {
      ...this.state.validationData,
      ...result.validationData,
    };
    this.setState({ validationData });
    this.props.updateFormValidity(!result.isGroupInvalid);
  };

  private renderInput(key: keyof Omit<IEmailForm, "message">, label: string) {
    const { isFormSubmitted } = this.props;
    const { validationData, form } = this.state;

    return (
      <Input
        id={key}
        name={key}
        type="text"
        label={label}
        value={form[key]}
        valueChanged={this.inputChanged(key)}
        invalid={validationData[key].invalid}
        isFormSubmitted={isFormSubmitted}
        validationMessage={validationData[key].validationMessage}
      />
    );
  }

  private inputChanged = (key: keyof IEmailForm) => {
    return (value: string) => {
      const form = { ...this.state.form };

      form[key] = value;

      this.setState({ form }, () => VALIDATE.call());
      this.props.updateForm(form);
    };
  };

  private getFormConfiguration = (
    content: IEmailFormContent,
    form: IEmailForm
  ) => {
    const config: IInputConfig[] = [];
    const validationFunctions = this.getValidationFunction(content);

    Object.keys(form).forEach((key) => {
      const value = form[key] || "";

      config.push({
        name: key,
        value,
        validators: validationFunctions[key],
      });
    });

    return config;
  };

  private getValidationFunction = (content: IEmailFormContent) => {
    const validators = {
      message: [],
      subject: [
        requiredNonEmptyString(content.emailFormSubjectRequired),
        maxLength(
          MAX_SUBJECT_LENGTH,
          content.emailFormSubjectMaxLength.replace(
            "{maxLength}",
            `${MAX_SUBJECT_LENGTH}`
          )
        ),
      ],
      recipients: [
        requiredNonEmptyString(content.emailFormRecipientsRequired),
        validateAmountOfSplitValues(
          MAX_RECIPIENTS_COUNT,
          content.emailFormRecipientsMaxCount
        ),
        validateSplitStringItemsLength(
          MAX_EMAIL_LENGTH,
          content.emailFormRecipientsSingleMaxLength
        ),
        validateSplitStringOfEmails(content.emailFormRecipientsSingleValid),
      ],
      senderName: [
        requiredNonEmptyString(content.emailFormSenderNameRequired),
        maxLength(
          MAX_NAME_LENGTH,
          content.emailFormSenderNameMaxLength.replace(
            "{maxLength}",
            `${MAX_NAME_LENGTH}`
          )
        ),
      ],
      senderEmail: [
        requiredNonEmptyString(content.emailFormSenderEmailRequired),
        pattern(Patterns.email, content.emailFormSenderEmailValidEmail),
        maxLength(
          MAX_EMAIL_LENGTH,
          content.emailFormSenderEmailMaxLength.replace(
            "{maxLength}",
            `${MAX_EMAIL_LENGTH}`
          )
        ),
      ],
    };

    return validators;
  };
}
